import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Clock, Calendar, AlertCircle, Check } from 'lucide-react';
import { format, addDays, subDays, isValid, parseISO } from 'date-fns';
import { createPayrollRequest, checkExistingPayrollEntry } from '../lib/payrollRequests';
import toast from 'react-hot-toast';
import { usePayrollHoursText } from '../hooks/usePayrollHoursText';

interface AddMissingEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  resourceId: string;
  businessId: string;
  onSuccess: () => void;
}

const AddMissingEntryModal: React.FC<AddMissingEntryModalProps> = ({
  isOpen,
  onClose,
  resourceId,
  businessId,
  onSuccess
}) => {
  const [workDate, setWorkDate] = useState('');
  const [clockIn, setClockIn] = useState('');
  const [clockOut, setClockOut] = useState('');
  const [breakStart, setBreakStart] = useState('');
  const [breakEnd, setBreakEnd] = useState('');
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingEntryCheck, setExistingEntryCheck] = useState<{loading: boolean, exists: boolean}>({
    loading: false,
    exists: false
  });
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  // Open animation
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isVisible && !isOpen) {
      setIsAnimating(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(), 200);
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setWorkDate('');
      setClockIn('');
      setClockOut('');
      setBreakStart('');
      setBreakEnd('');
      setNotes('');
      setReason('');
      setErrors({});
      setExistingEntryCheck({ loading: false, exists: false });
    }
  }, [isOpen]);

  // Check for existing entry when work date changes
  useEffect(() => {
    const checkExisting = async () => {
      if (!workDate || !resourceId || !businessId) return;
      
      setExistingEntryCheck({ loading: true, exists: false });
      
      try {
        const exists = await checkExistingPayrollEntry(resourceId, businessId, workDate);
        setExistingEntryCheck({ loading: false, exists });
        
        if (exists) {
          setErrors(prev => ({ ...prev, workDate: 'An entry already exists for this date' }));
        } else {
          setErrors(prev => {
            const { workDate, ...rest } = prev;
            return rest;
          });
        }
      } catch (error) {
        console.error('Error checking existing entry:', error);
        setExistingEntryCheck({ loading: false, exists: false });
      }
    };

    checkExisting();
  }, [workDate, resourceId, businessId]);

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!workDate) {
      newErrors.workDate = 'Work date is required';
    } else {
      const today = new Date();
      const selectedDate = new Date(workDate);
      if (selectedDate > today) {
        newErrors.workDate = 'Cannot add future entries';
      }
    }

    if (!clockIn) {
      newErrors.clockIn = 'Clock in time is required';
    }

    if (!clockOut) {
      newErrors.clockOut = 'Clock out time is required';
    }

    if (clockIn && clockOut) {
      const clockInTime = new Date(`${workDate}T${clockIn}`);
      const clockOutTime = new Date(`${workDate}T${clockOut}`);
      
      if (clockInTime >= clockOutTime) {
        newErrors.clockOut = 'Clock out must be after clock in';
      }
    }

    if (breakStart && breakEnd) {
      const breakStartTime = new Date(`${workDate}T${breakStart}`);
      const breakEndTime = new Date(`${workDate}T${breakEnd}`);
      
      if (breakStartTime >= breakEndTime) {
        newErrors.breakEnd = 'Break end must be after break start';
      }
      
      if (clockIn && clockOut) {
        const clockInTime = new Date(`${workDate}T${clockIn}`);
        const clockOutTime = new Date(`${workDate}T${clockOut}`);
        
        if (breakStartTime < clockInTime || breakEndTime > clockOutTime) {
          newErrors.breakStart = 'Break must be within work hours';
        }
      }
    }

    if (!reason.trim()) {
      newErrors.reason = 'Please provide a reason for this request';
    }

    if (existingEntryCheck.exists) {
      newErrors.workDate = 'An entry already exists for this date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateTotals = () => {
    if (!clockIn || !clockOut) return { minutes: 0, hours: 0, earnings: 0 };

    let totalMs = new Date(`${workDate}T${clockOut}`).getTime() - new Date(`${workDate}T${clockIn}`).getTime();
    
    if (breakStart && breakEnd) {
      const breakMs = new Date(`${workDate}T${breakEnd}`).getTime() - new Date(`${workDate}T${breakStart}`).getTime();
      totalMs -= breakMs;
    }
    
    const totalMinutes = Math.max(0, Math.floor(totalMs / (1000 * 60)));
    const hours = totalMinutes / 60;
    
    return {
      minutes: totalMinutes,
      hours: Math.round(hours * 100) / 100,
      earnings: 0
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const createLocalTimestamp = (date: string, time: string) => {
        const [year, month, day] = date.split('-').map(Number);
        const [hours, minutes] = time.split(':').map(Number);
        const localDate = new Date(year, month - 1, day, hours, minutes);
        return localDate.toISOString();
      };

      const requestData = {
        business_id: businessId,
        resource_id: resourceId,
        request_type: 'add' as const,
        requested_work_date: workDate,
        requested_clock_in: createLocalTimestamp(workDate, clockIn),
        requested_clock_out: createLocalTimestamp(workDate, clockOut),
        requested_break_start: breakStart ? createLocalTimestamp(workDate, breakStart) : undefined,
        requested_break_end: breakEnd ? createLocalTimestamp(workDate, breakEnd) : undefined,
        requested_notes: notes || undefined,
        request_reason: reason,
      };

      await createPayrollRequest(requestData);
      
      toast.success('Missing entry request submitted successfully!');
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error submitting missing entry request:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const totals = calculateTotals();
  const hoursText = usePayrollHoursText([totals.minutes]);

  if (!isOpen && !isAnimating) return null;

  return createPortal(
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isVisible ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{ 
        zIndex: 100200,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)'
      }}
      onClick={handleClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`bg-white transform transition-all duration-200 overflow-hidden ${
          isMobile 
            ? `fixed left-0 right-0 w-full ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isVisible ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100210,
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          ...(isMobile && { left: 0, right: 0, maxWidth: '100vw' }),
          ...(!isMobile && { height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }),
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative flex-shrink-0">
            <h2 className="text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
              Add Missing Entry
            </h2>
            <p className="text-sm text-gray-500 mt-1">Submit a request for a missed time entry</p>
          </div>

          {/* Content */}
          <form 
            id="missing-entry-form" 
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto overflow-x-hidden bg-white"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-5`}>
              {/* Work Date */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Work Date *
                </label>
                <input
                  type="date"
                  value={workDate}
                  onChange={(e) => setWorkDate(e.target.value)}
                  max={format(new Date(), 'yyyy-MM-dd')}
                  className={`w-full max-w-full px-3 py-3 text-base bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 box-border ${
                    errors.workDate ? 'border-red-300' : 'border-gray-300'
                  }`}
                  style={{ fontSize: '16px' }}
                />
                {existingEntryCheck.loading && (
                  <p className="mt-2 text-sm text-gray-500">Checking for existing entries...</p>
                )}
                {existingEntryCheck.exists && (
                  <div className="mt-2 flex items-center gap-1.5 text-sm text-red-600">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    An entry already exists for this date
                  </div>
                )}
                {errors.workDate && !existingEntryCheck.exists && (
                  <p className="mt-2 text-sm text-red-600">{errors.workDate}</p>
                )}
              </div>

              {/* Time Inputs */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Work Hours *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="min-w-0">
                    <label className="block text-xs text-gray-500 mb-1.5">Clock In</label>
                    <input
                      type="time"
                      value={clockIn}
                      onChange={(e) => setClockIn(e.target.value)}
                      className={`w-full max-w-full px-3 py-3 text-base bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 box-border ${
                        errors.clockIn ? 'border-red-300' : 'border-gray-300'
                      }`}
                      style={{ fontSize: '16px' }}
                    />
                    {errors.clockIn && <p className="mt-1 text-xs text-red-600">{errors.clockIn}</p>}
                  </div>
                  <div className="min-w-0">
                    <label className="block text-xs text-gray-500 mb-1.5">Clock Out</label>
                    <input
                      type="time"
                      value={clockOut}
                      onChange={(e) => setClockOut(e.target.value)}
                      className={`w-full max-w-full px-3 py-3 text-base bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 box-border ${
                        errors.clockOut ? 'border-red-300' : 'border-gray-300'
                      }`}
                      style={{ fontSize: '16px' }}
                    />
                    {errors.clockOut && <p className="mt-1 text-xs text-red-600">{errors.clockOut}</p>}
                  </div>
                </div>
              </div>

              {/* Break Time */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Break Time <span className="font-normal text-gray-400">(Optional)</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="min-w-0">
                    <label className="block text-xs text-gray-500 mb-1.5">Break Start</label>
                    <input
                      type="time"
                      value={breakStart}
                      onChange={(e) => setBreakStart(e.target.value)}
                      className={`w-full max-w-full px-3 py-3 text-base bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 box-border ${
                        errors.breakStart ? 'border-red-300' : 'border-gray-300'
                      }`}
                      style={{ fontSize: '16px' }}
                    />
                    {errors.breakStart && <p className="mt-1 text-xs text-red-600">{errors.breakStart}</p>}
                  </div>
                  <div className="min-w-0">
                    <label className="block text-xs text-gray-500 mb-1.5">Break End</label>
                    <input
                      type="time"
                      value={breakEnd}
                      onChange={(e) => setBreakEnd(e.target.value)}
                      className={`w-full max-w-full px-3 py-3 text-base bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 box-border ${
                        errors.breakEnd ? 'border-red-300' : 'border-gray-300'
                      }`}
                      style={{ fontSize: '16px' }}
                    />
                    {errors.breakEnd && <p className="mt-1 text-xs text-red-600">{errors.breakEnd}</p>}
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Notes <span className="font-normal text-gray-400">(Optional)</span>
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full max-w-full px-3 py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 resize-none box-border"
                  placeholder="Any additional notes about this entry..."
                  style={{ fontSize: '16px' }}
                />
              </div>

              {/* Reason */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Reason for Request *
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  className={`w-full max-w-full px-3 py-3 text-base bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 resize-none box-border ${
                    errors.reason ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Please explain why this entry is missing (e.g., forgot to clock in, system issue, etc.)"
                  style={{ fontSize: '16px' }}
                />
                {errors.reason && <p className="mt-2 text-sm text-red-600">{errors.reason}</p>}
              </div>

              {/* Summary */}
              {totals.minutes > 0 && (
                <div className="bg-blue-50 rounded-[12px] border border-blue-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                  <h3 className="text-sm font-semibold text-blue-900 mb-2">Entry Summary</h3>
                  <div className="space-y-1 text-sm text-blue-700">
                    <p><strong>Total Time:</strong> {hoursText(totals.minutes)}</p>
                    <p><strong>Total Minutes:</strong> {totals.minutes} minutes</p>
                    <p className="text-xs text-blue-600 mt-1">Earnings will be calculated based on your hourly rate</p>
                  </div>
                </div>
              )}
            </div>
          </form>

          {/* Footer */}
          <div className="border-t border-gray-200 bg-white px-4 py-3 flex-shrink-0" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0.75rem))' }}>
            <div className="flex gap-2.5">
              <button
                type="button"
                onClick={handleClose}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 active:bg-gray-100 active:scale-[0.98] text-[15px] font-semibold transition-all duration-150 touch-manipulation whitespace-nowrap"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="missing-entry-form"
                disabled={isSubmitting || existingEntryCheck.exists || existingEntryCheck.loading}
                className="flex-1 py-2.5 px-4 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl text-[15px] font-semibold shadow-sm transition-all duration-150 touch-manipulation flex items-center justify-center gap-1.5 whitespace-nowrap"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent flex-shrink-0" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span>Submit Request</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddMissingEntryModal;
